import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Divider,
  Alert as MuiAlert,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete
} from '@mui/material';
import { CheckCircle, Build, Edit } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { format, addDays } from 'date-fns';
import axios from 'axios';

import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { DEFAULT_EXPERIMENT_WEEKS } from '../../constants/experiments';
import AICandidateChip from '../shared/AICandidateChip';

/**
 * AlertRow - Inline alert action component for the ProductAIActionPopover
 *
 * State machine:
 * - initial: "Is this broken?" with Yes/No buttons
 * - confirm: "Create experiment?" with Yes/No buttons (after clicking "Not broken")
 * - experiment_form: Inline form for creating experiment with AI recommendations
 * - date_picker: Date picker for planned resolution (after clicking "Broken")
 * - under_review: Shows scheduled date + "Mark repaired" button
 */
const AlertRow = ({
  alert,
  clientId,
  storeId,
  productId,
  productName,
  onAlertUpdated,
  onContentChange
}) => {
  const { t, i18n } = useTranslation();

  // Determine initial step based on alert status
  const getInitialStep = () => {
    if (alert.status === 'under_review') return 'under_review';
    return 'initial';
  };

  const [step, setStep] = useState(getInitialStep);
  const [selectedDate, setSelectedDate] = useState(addDays(new Date(), 1));
  const [plannedDate, setPlannedDate] = useState(
    alert.resolutionDate ? new Date(alert.resolutionDate) : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Experiment form state - default to replacement since alerts are typically about non-selling products
  const [experimentType, setExperimentType] = useState('reemplazo_producto');
  const [discount, setDiscount] = useState('');
  const [dateFrom, setDateFrom] = useState(addDays(new Date(), 10));
  const [dateTo, setDateTo] = useState(addDays(new Date(), 10 + DEFAULT_EXPERIMENT_WEEKS * 7));
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingAiSuggestion, setLoadingAiSuggestion] = useState(false);

  // Replacement experiment state
  const [replacementProducts, setReplacementProducts] = useState([]);
  const [selectedReplacement, setSelectedReplacement] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Fetch AI recommendation based on experiment type
  const fetchAiRecommendation = useCallback(async (type = experimentType) => {
    setLoadingAiSuggestion(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_BASE_URL}/experiments/candidate-suggestion`,
        {
          params: {
            clientId,
            storeId,
            productId,
            experimentType: type
          },
          headers: createAuthHeader(token)
        }
      );
      setAiSuggestion(response.data);
      // Delay to allow DOM to render before repositioning
      setTimeout(() => onContentChange?.(), 100);
    } catch (err) {
      console.error('Failed to fetch AI recommendation:', err);
    } finally {
      setLoadingAiSuggestion(false);
    }
  }, [clientId, storeId, productId, experimentType, onContentChange]);

  // Fetch replacement products
  const fetchReplacementProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_BASE_URL}/all-products`,
        {
          params: { clientId },
          headers: createAuthHeader(token)
        }
      );
      setReplacementProducts(response.data?.products || []);
    } catch (err) {
      console.error('Failed to fetch replacement products:', err);
    } finally {
      setLoadingProducts(false);
    }
  }, [clientId]);

  // Handle experiment type change
  const handleExperimentTypeChange = (event, newType) => {
    if (newType) {
      setExperimentType(newType);
      setAiSuggestion(null);
      onContentChange?.(); // Reposition popover when type changes
      fetchAiRecommendation(newType);
      if (newType === 'reemplazo_producto' && replacementProducts.length === 0) {
        fetchReplacementProducts();
      }
    }
  };

  // Handle "Not Broken" click - go to confirm step
  const handleNotBroken = () => {
    setStep('confirm');
    onContentChange?.();
  };

  // Handle "No" to experiment creation - just mark as not broken
  const handleConfirmNotBroken = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_BASE_URL}/alerts/update?clientId=${clientId}`,
        {
          alertIds: [alert.id],
          action: 'confirm_not_broken',
          metadata: {}
        },
        { headers: createAuthHeader(token) }
      );
      onAlertUpdated?.();
    } catch (err) {
      console.error('Failed to mark as not broken:', err);
      setError(t('errorProcessingAction', { defaultValue: 'Error processing action' }));
    } finally {
      setLoading(false);
    }
  };

  // Handle "Yes" to experiment creation - show experiment form
  const handleShowExperimentForm = async () => {
    setStep('experiment_form');
    onContentChange?.();
    // Fetch both AI recommendation and replacement products since replacement is the default
    await Promise.all([
      fetchAiRecommendation('reemplazo_producto'),
      fetchReplacementProducts()
    ]);
  };

  // Handle saving experiment
  const handleSaveExperiment = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');

      // Step 1: Mark alert as not broken
      await axios.post(
        `${API_BASE_URL}/alerts/update?clientId=${clientId}`,
        {
          alertIds: [alert.id],
          action: 'confirm_not_broken',
          metadata: {}
        },
        { headers: createAuthHeader(token) }
      );

      // Step 2: Create experiment
      const experimentData = {
        dStoreId: storeId,
        dProductId: productId,
        experimentType,
        discount: experimentType === 'promo_precio_unitario' ? Number(discount) : 0,
        newDProductId: experimentType === 'reemplazo_producto' ? selectedReplacement?.id : null,
        dateFrom: format(dateFrom, 'yyyy-MM-dd'),
        dateTo: format(dateTo, 'yyyy-MM-dd'),
        status: 'pending',
        alertId: alert.id,
        isAi: Boolean(aiSuggestion?.suggestedDiscount || aiSuggestion?.candidates?.length)
      };

      await axios.post(
        `${API_BASE_URL}/experiments/create?clientId=${clientId}`,
        experimentData,
        { headers: createAuthHeader(token) }
      );

      onAlertUpdated?.();
    } catch (err) {
      console.error('Failed to create experiment:', err);
      setError(err.response?.data?.message || t('errorProcessingAction', { defaultValue: 'Error processing action' }));
    } finally {
      setLoading(false);
    }
  };

  // Handle "Broken" click - show date picker
  const handleBroken = () => {
    setStep('date_picker');
    onContentChange?.();
  };

  // Submit planned repair date
  const handleDateSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_BASE_URL}/alerts/update?clientId=${clientId}`,
        {
          alertIds: [alert.id],
          action: 'confirm_broken',
          metadata: {
            planned_resolution_date: format(selectedDate, 'yyyy-MM-dd')
          }
        },
        { headers: createAuthHeader(token) }
      );
      setPlannedDate(selectedDate);
      setStep('under_review');
      onContentChange?.();
      onAlertUpdated?.();
    } catch (err) {
      console.error('Failed to mark as broken:', err);
      setError(t('errorProcessingAction', { defaultValue: 'Error processing action' }));
    } finally {
      setLoading(false);
    }
  };

  // Mark as repaired
  const handleMarkRepaired = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_BASE_URL}/alerts/update?clientId=${clientId}`,
        {
          alertIds: [alert.id],
          action: 'set_resolution_date',
          metadata: {
            resolution_date: format(plannedDate || new Date(), 'yyyy-MM-dd')
          }
        },
        { headers: createAuthHeader(token) }
      );
      onAlertUpdated?.();
    } catch (err) {
      console.error('Failed to mark as repaired:', err);
      setError(t('errorProcessingAction', { defaultValue: 'Error processing action' }));
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDisplayDate = (date) => {
    if (!date) return '—';
    try {
      return format(new Date(date), i18n.language === 'es' ? 'dd/MM/yyyy' : 'MM/dd/yyyy');
    } catch {
      return '—';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Alert metrics - matching problem metrics style */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {/* Weeks without sales metric */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Typography sx={{ fontSize: '14px', color: '#f44336', minWidth: '16px', textAlign: 'center', lineHeight: 1.2 }}>
            ↓
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
              {alert.weeks_since} {t('weeksNoSales', { defaultValue: 'semanas sin ventas' })}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.2 }}>
              {t('noSalesDetected', { defaultValue: 'Producto sin movimiento detectado' })}
            </Typography>
          </Box>
        </Box>

        {/* Estimated loss metric */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Typography sx={{ fontSize: '14px', color: '#ff9800', minWidth: '16px', textAlign: 'center', lineHeight: 1.2 }}>
            △
          </Typography>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
              ~{Math.round(alert.estimatedLossWeeklyUnits)} {t('unitsPerWeek', { defaultValue: 'unid/sem' })}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.2 }}>
              {t('estimatedLoss', { defaultValue: 'Pérdida estimada semanal' })}
            </Typography>
          </Box>
        </Box>

        {/* Selection code metric (if available) */}
        {alert.selectionCode?.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Typography sx={{ fontSize: '14px', color: '#6b7280', minWidth: '16px', textAlign: 'center', lineHeight: 1.2 }}>
              •
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                {t('selectionCode', { defaultValue: 'Código de selección' })}: {alert.selectionCode.join(', ')}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <Divider sx={{ my: 0.5 }} />

      {/* Error message */}
      {error && (
        <MuiAlert severity="error" sx={{ fontSize: '12px', py: 0.5 }}>
          {error}
        </MuiAlert>
      )}

      {/* Step: Initial - Show Yes/No buttons */}
      {step === 'initial' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500 }}>
            {t('isBroken', { defaultValue: '¿Está averiado?' })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleNotBroken}
              disabled={loading}
              startIcon={<CheckCircle sx={{ fontSize: 14 }} />}
              sx={{ fontSize: '11px', flex: 1, textTransform: 'none' }}
            >
              {t('no', { defaultValue: 'No' })}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleBroken}
              disabled={loading}
              startIcon={<Build sx={{ fontSize: 14 }} />}
              sx={{ fontSize: '11px', flex: 1, textTransform: 'none', color: '#f97316', borderColor: '#f97316', '&:hover': { borderColor: '#ea580c', backgroundColor: 'rgba(249, 115, 22, 0.04)' } }}
            >
              {t('yes', { defaultValue: 'Sí' })}
            </Button>
          </Box>
        </Box>
      )}

      {/* Step: Confirm - "Create experiment?" */}
      {step === 'confirm' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500 }}>
            {t('createExperimentQuestion', { defaultValue: '¿Crear experimento?' })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleConfirmNotBroken}
              disabled={loading}
              sx={{ fontSize: '11px', flex: 1, textTransform: 'none' }}
            >
              {loading ? <CircularProgress size={14} /> : t('no', { defaultValue: 'No' })}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleShowExperimentForm}
              disabled={loading}
              sx={{ fontSize: '11px', flex: 1, textTransform: 'none' }}
            >
              {t('yes', { defaultValue: 'Sí' })}
            </Button>
          </Box>
          <Button
            size="small"
            onClick={() => { setStep('initial'); onContentChange?.(); }}
            disabled={loading}
            sx={{ fontSize: '11px', textTransform: 'none', color: 'text.secondary' }}
          >
            {t('cancel', { defaultValue: 'Cancelar' })}
          </Button>
        </Box>
      )}

      {/* Step: Experiment form - Inline creation */}
      {step === 'experiment_form' && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Experiment type toggle */}
            <ToggleButtonGroup
              value={experimentType}
              exclusive
              onChange={handleExperimentTypeChange}
              size="small"
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  fontSize: '11px',
                  textTransform: 'none',
                  py: 0.5
                }
              }}
            >
              <ToggleButton value="promo_precio_unitario">
                {t('discount', { defaultValue: 'Descuento' })}
              </ToggleButton>
              <ToggleButton value="reemplazo_producto">
                {t('replacement', { defaultValue: 'Reemplazo' })}
              </ToggleButton>
            </ToggleButtonGroup>

            {/* AI Recommendation chip - using AICandidateChip from ExperimentManagement */}
            {loadingAiSuggestion && (
              <Box sx={{ mb: 1 }}>
                <AICandidateChip
                  text={t('loading', { defaultValue: 'Cargando recomendación...' })}
                  loading
                  fullWidth
                />
              </Box>
            )}
            {!loadingAiSuggestion && aiSuggestion?.suggestedDiscount && experimentType === 'promo_precio_unitario' && (
              <Box sx={{ mb: 1 }}>
                <AICandidateChip
                  text={`${t('aiRecommendation', { defaultValue: 'Recomendación AI' })}: **${aiSuggestion.suggestedDiscount}%** ${t('discount', { defaultValue: 'descuento' })}`}
                  onClick={() => setDiscount(String(aiSuggestion.suggestedDiscount))}
                  fullWidth
                />
              </Box>
            )}
            {/* Replacement AI suggestions - show all candidates */}
            {!loadingAiSuggestion && aiSuggestion?.candidates?.length > 0 && experimentType === 'reemplazo_producto' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                {/* Text suggestion */}
                <AICandidateChip
                  text={
                    selectedReplacement
                      ? aiSuggestion.candidates.find(c => c.dProductId === selectedReplacement.id)?.suggestion || aiSuggestion.candidates[0]?.suggestion
                      : aiSuggestion.candidates[0]?.suggestion
                  }
                  isTextChip
                  fullWidth
                />
                {/* Candidate chips */}
                {aiSuggestion.candidates.map((candidate) => (
                  <AICandidateChip
                    key={candidate.dProductId || candidate.productId}
                    text={candidate.productName}
                    variant="candidate"
                    fullWidth
                    selected={selectedReplacement?.id === (candidate.dProductId || candidate.productId)}
                    onClick={() => {
                      setSelectedReplacement({
                        id: candidate.dProductId || candidate.productId,
                        name: candidate.productName
                      });
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Dates */}
            <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(2, 1fr)', mb: 1 }}>
              <DatePicker
                label={t('startDate', { defaultValue: 'Inicio' })}
                value={dateFrom}
                onChange={(val) => {
                  setDateFrom(val);
                  if (val) setDateTo(addDays(val, DEFAULT_EXPERIMENT_WEEKS * 7));
                }}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label={t('endDate', { defaultValue: 'Fin' })}
                value={dateTo}
                onChange={setDateTo}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Box>

            {/* Discount field (for discount type) */}
            {experimentType === 'promo_precio_unitario' && (
              <TextField
                label={t('discount', { defaultValue: 'Descuento' }) + ' (%)'}
                size="small"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                type="number"
                inputProps={{ min: 1, max: 90, step: 1 }}
              />
            )}

            {/* Replacement product selector (for replacement type) */}
            {experimentType === 'reemplazo_producto' && (
              <Autocomplete
                size="small"
                options={replacementProducts}
                getOptionLabel={(option) => option.name || ''}
                value={selectedReplacement}
                onChange={(event, newValue) => setSelectedReplacement(newValue)}
                loading={loadingProducts}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('selectNewProduct', { defaultValue: 'Seleccionar producto' })}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingProducts ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 0.5 }}>
              <Button
                size="small"
                onClick={() => { setStep('initial'); onContentChange?.(); }}
                disabled={loading}
                sx={{ fontSize: '11px', textTransform: 'none' }}
              >
                {t('cancel', { defaultValue: 'Cancelar' })}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveExperiment}
                disabled={
                  loading ||
                  (experimentType === 'promo_precio_unitario' && (!discount || Number(discount) < 1)) ||
                  (experimentType === 'reemplazo_producto' && !selectedReplacement)
                }
                sx={{ fontSize: '11px', textTransform: 'none' }}
              >
                {loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : t('save', { defaultValue: 'Guardar' })}
              </Button>
            </Box>
          </Box>
        </LocalizationProvider>
      )}

      {/* Step: Date picker - When will you fix it? */}
      {step === 'date_picker' && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500 }}>
              {t('whenWillYouFix', { defaultValue: '¿Cuándo lo arreglarás?' })}
            </Typography>
            <DatePicker
              label={t('plannedDate', { defaultValue: 'Fecha planificada' })}
              value={selectedDate}
              onChange={setSelectedDate}
              slotProps={{ textField: { size: 'small' } }}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={() => { setStep('initial'); onContentChange?.(); }}
                sx={{ fontSize: '11px', textTransform: 'none' }}
              >
                {t('cancel', { defaultValue: 'Cancelar' })}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleDateSubmit}
                disabled={loading || !selectedDate}
                sx={{ fontSize: '11px', textTransform: 'none' }}
              >
                {loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : t('save', { defaultValue: 'Guardar' })}
              </Button>
            </Box>
          </Box>
        </LocalizationProvider>
      )}

      {/* Step: Under review - Show scheduled date and mark repaired button */}
      {step === 'under_review' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
              {t('scheduledFor', { defaultValue: 'Programado' })}:
            </Typography>
            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'text.primary' }}>
              {formatDisplayDate(plannedDate)}
            </Typography>
            <IconButton
              size="small"
              onClick={() => { setStep('date_picker'); onContentChange?.(); }}
              sx={{ p: 0.5 }}
            >
              <Edit sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
          <Button
            size="small"
            variant="contained"
            onClick={handleMarkRepaired}
            disabled={loading}
            sx={{
              fontSize: '11px',
              textTransform: 'none',
              backgroundColor: '#4caf50',
              '&:hover': { backgroundColor: '#43a047' }
            }}
          >
            {loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : t('markRepaired', { defaultValue: 'Marcar como reparado' })}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AlertRow;
